import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import type { TFunction } from "i18next";
import {
  formatCompact,
  formatNumber,
  formatPercent,
} from "./clientFormatViewClient";

// Las cifras principales de una campaña o subcampaña. Se ven en su sitio y,
// cuando llegan arriba de la pantalla, se apartan al margen derecho en un panel
// pequeño que se queda fijo mientras se baja. Si el margen no da para el panel,
// van en una tira arriba a la derecha.

export interface StatItem {
  key: string;
  label: string;
  value: string;
  hint?: string;
}

/**
 * Cifras de resultados: impresiones, clics, CTR y las vistas completas,
 * conversiones e interacciones que haya; con `goal`, cuánto lleva del objetivo.
 */
export const performanceStatItems = (
  totals: {
    impressions: number;
    clicks: number;
    completeViews: number;
    conversions: number;
    engagements: number;
  },
  t: TFunction,
  language: string,
  goal?: { delivered: number; planned: number; kpiLabel: string }
): StatItem[] => {
  const count = (key: keyof typeof totals) => ({
    key,
    label: t(`clientView.stats.${key}`),
    // Las impresiones, exactas; el resto, abreviado.
    value:
      key === "impressions"
        ? formatNumber(totals[key], language)
        : formatCompact(totals[key], language),
  });
  return [
    count("impressions"),
    count("clicks"),
    ...(totals.impressions > 0
      ? [
          {
            key: "ctr",
            label: t("clientView.stats.ctr"),
            value: formatPercent(
              totals.clicks / totals.impressions,
              language,
              2
            ),
            hint: t("clientView.stats.ctrHint"),
          },
        ]
      : []),
    ...(totals.completeViews > 0 ? [count("completeViews")] : []),
    ...(totals.conversions > 0 ? [count("conversions")] : []),
    ...(totals.engagements > 0 ? [count("engagements")] : []),
    ...(goal
      ? [
          {
            key: "goalReached",
            label: t("clientView.stats.goalReached"),
            value: formatPercent(goal.delivered / goal.planned, language),
            hint: t("clientView.stats.goalReachedHint", {
              delivered: formatCompact(goal.delivered, language),
              planned: formatCompact(goal.planned, language),
              kpi: goal.kpiLabel.toLowerCase(),
            }),
          },
        ]
      : []),
  ];
};

const PANEL_WIDTH = 200;
const PANEL_MAX_WIDTH = 240;
const GAP = 24;
const TOP = 20;

type Dock =
  | { mode: "side"; left: number; width: number }
  | { mode: "top"; right: number; maxWidth: number };

const sameDock = (a: Dock | null, b: Dock | null) =>
  JSON.stringify(a) === JSON.stringify(b);

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: none; }
`;

const DockItem: React.FC<{ item: StatItem; inline?: boolean }> = ({
  item,
  inline,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        fontSize: 11,
        color: "text.secondary",
        lineHeight: 1.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {item.label}
    </Typography>
    <Typography
      sx={{
        fontSize: inline ? 14 : 16,
        fontWeight: 700,
        lineHeight: 1.25,
        letterSpacing: "-0.01em",
        whiteSpace: "nowrap",
      }}
    >
      {item.value}
    </Typography>
  </Box>
);

/**
 * `children`: las cifras en su sitio (sus paneles). `groups`: las mismas, por
 * grupos, para el panel que se queda fijo.
 */
const StickyStatsViewClient: React.FC<{
  groups: StatItem[][];
  children: React.ReactNode;
}> = ({ groups, children }) => {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const [dock, setDock] = useState<Dock | null>(null);
  const shown = groups.filter((group) => group.length > 0);

  useEffect(() => {
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      let next: Dock | null = null;
      // En cuanto su parte de arriba llega al borde de la pantalla.
      if (rect.top < 0) {
        const margin = window.innerWidth - rect.right;
        next =
          margin >= PANEL_WIDTH + 2 * GAP
            ? {
                mode: "side",
                left: Math.round(rect.right + GAP),
                width: Math.min(PANEL_MAX_WIDTH, Math.round(margin - 2 * GAP)),
              }
            : {
                mode: "top",
                right: Math.max(GAP, Math.round(margin)),
                maxWidth: Math.round(rect.width),
              };
      }
      setDock((prev) => (sameDock(prev, next) ? prev : next));
    };
    update();
    // Con `capture` se oye también el scroll de cualquier contenedor.
    document.addEventListener("scroll", update, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", update);
    return () => {
      document.removeEventListener("scroll", update, { capture: true });
      window.removeEventListener("resize", update);
    };
  }, []);

  const panel = {
    position: "fixed",
    top: TOP,
    zIndex: theme.zIndex.appBar,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    animation: `${slideIn} 0.2s ease-out`,
    "@media print": { display: "none" },
  } as const;

  return (
    <>
      <Box
        ref={ref}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          // Se van a su panel: aquí se desvanecen.
          opacity: dock ? 0 : 1,
          transition: "opacity 0.2s ease",
        }}
      >
        {children}
      </Box>

      {/* Las mismas cifras, ya leídas arriba: el panel no se vuelve a leer. */}
      {dock?.mode === "side" && (
        <Box
          aria-hidden
          sx={{ ...panel, left: dock.left, width: dock.width, p: 2 }}
        >
          {shown.map((group, index) => (
            <Box
              key={group[0].key}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                ...(index > 0 && {
                  mt: 1.5,
                  pt: 1.5,
                  borderTop: `1px solid ${theme.palette.divider}`,
                }),
              }}
            >
              {group.map((item) => (
                <DockItem key={item.key} item={item} />
              ))}
            </Box>
          ))}
        </Box>
      )}
      {dock?.mode === "top" && (
        <Box
          aria-hidden
          sx={{
            ...panel,
            right: dock.right,
            maxWidth: dock.maxWidth,
            px: 2,
            py: 1.25,
            display: "flex",
            flexWrap: "wrap",
            columnGap: 2.5,
            rowGap: 1,
          }}
        >
          {shown.flat().map((item) => (
            <DockItem key={item.key} item={item} inline />
          ))}
        </Box>
      )}
    </>
  );
};

export default StickyStatsViewClient;
